/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 기타 유틸리티 함수 모음
 * 버전 : 1.0
 * 파일명 : Etc.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-12-26
 * ================================================================ 
 * 수정 : 2026-01-09, 강동형 : Tmap 연동기능 추가
 * 수정 : 2026-01-19, 강동형 : DialogFestivalSession 스타일 추가
 * 수정 : 2026-01-27, 강동형 : BottomSheetType에 추가된 값에 맞게 파싱값 추가
 * 수정 : 2026-02-02, 강동형 : BottomSheetType에 취업률 추가
 * ================================================================ */

import { BottomSheetType } from "src/types/SchoolType";
import styled from "styled-components";

// 파일 업로드 창 열기
export const openPicker = (input: HTMLInputElement | null) => {
    if (!input) return;

    // 같은 파일 다시 선택 가능하게 초기화
    input.value = "";
    input.click();
};

// 학교 데이터 파싱
export const parseSchoolData = (school: any): BottomSheetType => {
    if (school.isAi) {
        return {
        school_id: school.school_id || school.education_id,
        school_class: school.school_class,
        school_name: school.school_name || school.education_name,
        visited_at: school.visited_at,
        establish_date: school.establish_date,
        address: school.address,
        preferred_major: school.preferred_major,
        military_agreement: school.military_agreement,
        rotc: school.rotc,
        school_type: school.school_type || school.education_type,
        total_student_cnt: school.total_student_cnt,
        total_female_rate: school.total_female_rate,
        total_male_rate: school.total_male_rate,
        special_major_list: school.special_major_list,
        tel: school.tel,
        homepage: school.homepage,
        all_major_list : school.all_major_list,
        festival_info: school.festival_info,
        memo:school.memo,
        employment_rate : school.employment_rate,
        location_latitude: school.location_latitude,
        location_longitude: school.location_longitude,
        };
    }

    if (school.school_type) {
        return {
        school_id: school.school_id,
        school_class: school.school_class,
        school_name: school.school_name,
        visited_at: school.visited_at,
        wide_loc: school.wide_loc,
        city_loc: school.city_loc,
        establish_date: school.establish_date,
        address: school.address,
        location_latitude: school.location_latitude,
        location_longitude: school.location_longitude,
        preferred_major: school.preferred_major,
        military_agreement: school.military_agreement,
        school_type: school.school_type,
        total_student_cnt: school.total_student_cnt,
        total_female_rate: school.total_female_rate,
        total_male_rate: school.total_male_rate,
        special_major_list: school.special_major_list,
        interested_cnt: school.interested_cnt,
        class_cnt: school.class_cnt,
        excluded: school.excluded,
        tel: school.tel,
        homepage: school.homepage,
        all_major_list: school.all_major_list,
        festival_info: school.festival_info,
        memo:school.memo,
        employment_rate : school.employment_rate
        };
    }

    return {
        school_id: school.education_id,
        school_class: school.school_class,
        school_name: school.education_name,
        visited_at: school.visited_at,
        wide_loc: school.wide_loc,
        city_loc: school.city_loc,
        establish_date: school.establish_date,
        address: school.address,
        location_latitude: school.location_latitude,
        location_longitude: school.location_longitude,
        preferred_major: school.preferred_major,
        military_agreement: school.military_agreement,
        rotc: school.rotc,
        school_type: school.education_type,
        total_student_cnt: school.total_student_cnt,
        total_female_rate: school.total_female_rate,
        total_male_rate: school.total_male_rate,
        special_major_list: school.special_major_list,
        interested_cnt: school.interested_cnt,
        class_cnt: school.class_cnt,
        excluded: school.excluded,
        tel: school.tel,
        homepage: school.homepage,
        all_major_list: school.all_major_list,
        festival_info: school.festival_info,
        memo:school.memo,
        employment_rate : school.employment_rate
    };
};

type openTmapProps = {
    longitude : string | undefined
    latitude : string | undefined
}
// Tmap에서 열기
export const openTmap = ({longitude, latitude}:openTmapProps)=>{
    const url = `tmap://route?goalx=${longitude}&goaly=${latitude}`;

    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    const storeUrl = isAndroid
        ? "https://play.google.com/store/apps/details?id=com.skt.tmap.ku"
        : isIOS
        ? "https://apps.apple.com/kr/search?term=tmap"
        : "https://www.tmap.co.kr/";

    let opened = false;

    const markOpened = () => {
        opened = true;
        cleanup();
    };

    const onVisibility = () => {
        if (document.visibilityState === "hidden") markOpened();
    };

    const cleanup = () => {
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("pagehide", markOpened);
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", markOpened);

    // 1) 딥링크 시도
    window.location.href = url;

    // 2) 실패 시 “바로 스토어” (TMAP 웹 같은 중간 경유 없음)
    setTimeout(() => {
        if (!opened) window.location.href = storeUrl;
        cleanup();
    }, 1200);
}

interface SessionWrapperProps {
    session: '축제' | '박람회';
}

export const SessionWrapper = styled.div<SessionWrapperProps>`
    font-size: 12px;
    border: 1px solid ${({ session }) =>
        session === "축제"
        ? "#4069B0"
        : session === "박람회"
        ? "#AE72D1"
        : "#FFFFFF"};
    color: ${({ session }) =>
        session === "축제"
        ? "#4069B0"
        : session === "박람회"
        ? "#AE72D1"
        : "#FFFFFF"};
    padding: 3px;
    min-width: 60px;
    max-width: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 2px;

    background-color: ${({ session }) =>
        session === "축제"
        ? "#E0E7F2"
        : session === "박람회"
        ? "#F6E6FF"
        : "#FFFFFF"};
`;

export const toAbsoluteUrl = (raw?: string) => {
  if (!raw) return "";
  const s = raw.trim();

  // 이미 http/https면 그대로
  if (/^https?:\/\//i.test(s)) return s;

  // //example.com 형태면 https 붙이기
  if (s.startsWith("//")) return `https:${s}`;

  // www., example.com 같은 경우 https 붙이기
  return `https://${s}`;
};